import { createLogger } from "../../utils/logger";

const logger = createLogger("ManualOAuth2");
const LOG_CODE_PREVIEW_LENGTH = 20;

/**
 * Generate random string for PKCE
 */
const generateRandomString = (): string => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    let result = "";
    for (const byte of array) {
        result += byte.toString(16).padStart(2, "0");
    }
    return result;
};

/**
 * Generate SHA256 hash and base64url encode for PKCE
 */
const sha256 = async (plain: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    const hash = await crypto.subtle.digest("SHA-256", data);
    const hashArray = new Uint8Array(hash);
    let binary = "";
    for (const byte of hashArray) {
        binary += String.fromCharCode(byte);
    }
    const base64 = btoa(binary);
    return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
};

/**
 * Build Auth0 authorization URL with PKCE
 */
export const buildAuthorizationUrl = async (
    domain: string,
    clientId: string,
    redirectUri: string,
    audience?: string,
): Promise<string> => {
    // Generate PKCE values
    const codeVerifier = generateRandomString();
    const codeChallenge = await sha256(codeVerifier);
    const state = generateRandomString();

    // Store for callback
    sessionStorage.setItem("oauth2_code_verifier", codeVerifier);
    sessionStorage.setItem("oauth2_state", state);

    const params = new URLSearchParams({
        client_id: clientId,
        response_type: "code",
        redirect_uri: redirectUri,
        scope: "openid profile email offline_access",
        state,
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
        prompt: "login",
        ...(audience && { audience }),
    });

    const url = `https://${domain}/authorize?${params.toString()}`;
    logger.info("Built authorization URL:", {
        domain,
        clientId,
        redirectUri,
        audience,
        codeChallenge: `${codeChallenge.substring(0, 10)}...`,
        state: `${state.substring(0, 10)}...`,
    });

    return url;
};

/**
 * Exchange authorization code for tokens
 */
export const exchangeCodeForTokens = async (
    domain: string,
    clientId: string,
    redirectUri: string,
    code: string,
): Promise<string> => {
    const codeVerifier = sessionStorage.getItem("oauth2_code_verifier");

    if (!codeVerifier) {
        throw new Error("Code verifier not found. Please restart login flow.");
    }

    const body = new URLSearchParams({
        grant_type: "authorization_code",
        client_id: clientId,
        code,
        redirect_uri: redirectUri,
        code_verifier: codeVerifier,
    });

    logger.info("Exchanging code for tokens...", {
        domain,
        clientId,
        redirectUri,
        code: `${code.substring(0, LOG_CODE_PREVIEW_LENGTH)}...`,
        codeVerifier: `${codeVerifier.substring(0, 10)}...`,
    });

    const response = await fetch(`https://${domain}/oauth/token`, {
        method: "POST",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
        },
        body: body.toString(),
    });

    logger.info("Token exchange response:", {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
    });

    if (!response.ok) {
        const error = await response.text();
        logger.error("Token exchange failed:", { status: response.status, error });
        throw new Error(`Token exchange failed: ${response.status} ${error}`);
    }

    const data = await response.json();
    logger.info("Token exchange successful", {
        hasAccessToken: !!data.access_token,
        tokenType: data.token_type,
        expiresIn: data.expires_in,
    });

    sessionStorage.removeItem("oauth2_code_verifier");
    sessionStorage.removeItem("oauth2_state");

    return data.access_token;
};

/**
 * Parse OAuth2 callback from URL
 */
export const parseCallback = (): { code: string; state: string } | null => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const error = params.get("error");

    logger.info("Parsing callback URL:", {
        hasCode: !!code,
        hasState: !!state,
        hasError: !!error,
        url: window.location.href,
    });

    if (error) {
        const errorDescription = params.get("error_description");
        logger.error("OAuth2 error:", error, errorDescription);
        throw new Error(`OAuth2 error: ${error} - ${errorDescription}`);
    }

    if (!code || !state) {
        logger.info("No OAuth2 callback detected (no code or state)");
        return null;
    }

    // Verify state
    const storedState = sessionStorage.getItem("oauth2_state");
    logger.info("Verifying state:", {
        receivedState: `${state.substring(0, 10)}...`,
        storedState: `${storedState?.substring(0, 10)}...`,
        matches: state === storedState,
    });

    if (state !== storedState) {
        logger.error("State mismatch - possible CSRF attack");
        throw new Error("State mismatch. Please restart login.");
    }

    logger.info("OAuth2 callback parsed successfully");

    return { code, state };
};

/**
 * Clear OAuth2 parameters from URL
 */
export const clearCallbackParams = (): void => {
    const url = new URL(window.location.href);
    url.searchParams.delete("code");
    url.searchParams.delete("state");
    url.searchParams.delete("error");
    url.searchParams.delete("error_description");

    window.history.replaceState({}, document.title, url.pathname);
    logger.info("Cleared callback params from URL");
};
